import React, { useState, useEffect } from 'react';
import { Input, Dropdown, notification, DatePicker } from 'antd';
import { connect, useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  getProjectById,
  signedUploadUrl,
  isTicketOpen,
  isChatOpen,
  addSection,
  toggleNewProjectModal,
} from '../../actions';

import '../../assets/style/modal.css';

import { resetDrawer } from '../../actions/toggleAction';
import { FormattedMessage, useIntl } from 'react-intl';
import { addTaskAction, openTaskAction, uploadTaskUrlAction } from '../../redux/tasks/actions';
import TextEditorWrapper from '../../components/UI/molecules/editor/TextEditorWrapper';
import ConnectedProjectSelect from '../../components/UI/molecules/task-components/ConnectedProjectSelect';
import ConnectedAssigneeSelect from '../../components/UI/molecules/task-components/ConnectedAssigneeSelect';
import { prepareContentForSending } from '../../components/UI/molecules/editor/utils/editorContentOperations';
import { taskPriority, PRIORITY_COLORS, PRIORITY_LABEL, PRIORITY_ICON } from '../../js/constants';

const MAX_BYTES = 1 * 1024 ** 3;
const MAX_FILES = 4;

const AddTaskDrawer = ({ openProject, projects, defaultTitle, defaultAssignee }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedProject, setSelectedProject] = useState();
  const [selectedSection, setSelectedSection] = useState();

  const [sectionsMenu, setSectionsMenu] = useState([]);
  const [projectMentu, setProjectMenu] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState(
    defaultAssignee ? [{ value: defaultAssignee._id, label: defaultAssignee.fullName, avi: defaultAssignee.avi, email: defaultAssignee.email }] : []
  );

  const [taskName, setTaskName] = useState('');
  const [taskNameDescription, setTaskDescription] = useState(defaultTitle || '');

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dueDate, setDueDate] = useState(null);
  const [priority, setPriority] = useState('nill');

  const { users } = useSelector((state) => state.userReducer);

  const mentionOptions = users.map((user) => ({
    id: user._id,
    value: user.fullName,
    icon: user.avi,
  }));

  const handleCreate = () => {
    const assignees = selectedAssignees.map(a => a.value);
    onCreate(taskName, taskNameDescription, selectedProject?._id, selectedSection?._id, assignees);
    dispatch(resetDrawer());
  };

  useEffect(() => {
    setTaskDescription(defaultTitle);
  }, [defaultTitle]);

  useEffect(() => {
    // User should not be forced to select a project/section
    // if (!selectedProject && openProject?._id) {
    //   onProjectSelect(openProject);
    // } else if (projects?.length === 1) {
    //   onProjectSelect(projects[0]);
    // }

    const pMenu = projects?.map((project, i) => ({
      label: (
        <div
          onClick={() => {
            onProjectSelect(project);
          }}
          className="py-2 font-14"
        >
          {project?.title}
        </div>
      ),
      key: i,
    }));

    if (projects?.length <= 2) {
      pMenu.push({
        label: (
          <div
            className="p-1 bg-blue-500 rounded text-center text-white cursor-pointer hover:opacity-60"
            onClick={() => {
              dispatch(
                toggleNewProjectModal({
                  value: true,
                }),
              );
            }}
          >
            Create Project
          </div>
        ),
        key: -1,
      });
    }

    setProjectMenu(pMenu);
  }, []);

  const isButtonDisabled = !taskName;

  const openNewTicket = (newTask) => {
    const params = new URLSearchParams(location.search);
    if (!newTask) {
      dispatch(isTicketOpen(false));
      params.delete('selectedTask');
      const newUrl = `${location.pathname}?${params.toString()}`;
      navigate(newUrl);
      return;
    }
    dispatch(openTaskAction(true, newTask?.titleIdentifier));
  };

  const onCreate = async (
    taskName,
    taskNameDescription,
    Project,
    Section,
    Assignee,
  ) => {
    if (!taskName) {
      return;
    }

    const data = {
      taskTitle: taskName,
      taskDescription: prepareContentForSending(taskNameDescription),
      // section: Section,
      isCompleted: false,
      // projectId: Project,
      projects: [
        {
          projectId: Project,
          section: Section,
          position: 'top',
        },
      ],
      dueDate: dueDate ? dueDate.toISOString() : '',
      priority: priority !== 'nill' ? priority : undefined,
      addedTo: 'top',
    };
    //TODO: Osama handle multiple assignees
    data.assignee = [];
    for (let a of Assignee) {
      data.assignee.push({
        assigneeId: a,
      });
    }

    const uf = await uploadFiles(uploadedFiles);
    data.files = uf;
    const newTask = await dispatch(addTaskAction(data));
    openNewTicket(newTask);
  };

  const handleFileInputChange = (event) => {
    const incoming = Array.from(event.target.files || []);
    if (incoming.length === 0) return;

    // filter out too-large files
    const validSizeFiles = incoming.filter((file) => {
      if (file.size > MAX_BYTES) {
        notification.error({
          message: `"${file.name}" is larger than 1GB and was skipped.`,
          placement: 'bottomRight',
        });
        return false;
      }
      return true;
    });

    if (validSizeFiles.length === 0) {
      // reset input so same file can be reselected if needed
      event.target.value = null;
      return;
    }

    // respect max total files
    const availableSlots = MAX_FILES - (uploadedFiles?.length || 0);
    if (availableSlots <= 0) {
      notification.warning({
        message: `You can only upload up to ${MAX_FILES} files.`,
        placement: 'bottomRight',
      });
      event.target.value = null;
      return;
    }

    const toAdd = validSizeFiles.slice(0, availableSlots);
    if (validSizeFiles.length > availableSlots) {
      notification.warning({
        message: `Only ${availableSlots} of the selected files were added to stay within the ${MAX_FILES}-file limit.`,
        placement: 'bottomRight',
      });
    }

    setUploadedFiles((prev = []) => [...prev, ...toAdd]);

    // clear input to allow re-selection of same file later if needed
    event.target.value = null;
  };

  // Handle attachments
  const uploadFiles = async (files, task) => {
    const fileList = [];
    if (files?.length === 0 || !files) {
      return;
    }
    for (const file of files) {
      setIsLoading(true);
      const uploadTaskResponse = await dispatch(
        uploadTaskUrlAction(
          {
            fileName: file.name,
            type: file?.type,
            fileSize: file?.size,
            // taskId: task?._id,
          },
          undefined,
          intl,
        ),
      );
      const fileId = uploadTaskResponse?.fileId;
      const signedUrl = uploadTaskResponse?.signedUrl;
      if (signedUrl) {
        fileList.push(fileId);
        await dispatch(signedUploadUrl(signedUrl, file));
      }
    }

    let allFiles = fileList;

    // if (task?.files) {
    //   allFiles = fileList.concat(task?.files || []);
    // }

    return allFiles;
    // dispatch(
    //   editTaskAction(
    //     {
    //       _id: task._id,
    //       taskTitle: task.taskTitle,
    //       files: allFiles,
    //       attachments: []
    //     }, () => {
    //       setIsLoading(false);
    //     }
    //   ));
  };

  const onProjectSelect = (project) => {
    setSelectedProject(project);
    dispatch(
      getProjectById(project?._id, (res) => {
        const sections = [];
        res?.sections.forEach((section, i) => {
          sections.push({
            label: (
              <div
                onClick={() => setSelectedSection(section)}
                className="py-2 font-14"
              >
                <FormattedMessage
                  id={`${section.title}`.replaceAll(' ', '_').toUpperCase()}
                  defaultMessage={section.title}
                />
              </div>
            ),
            key: i,
          });
        });
        sections.push({
          label: (
            <div className="py-2 font-14" onClick={(e) => e.stopPropagation()}>
              <Input
                placeholder="Add Section"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    dispatch(
                      addSection(
                        {
                          title: e?.target?.value,
                          description: '',
                          project: project?._id,
                        },
                        (sec) => {
                          setSectionsMenu([...sections, sec]);
                          setSelectedSection(sec);
                        },
                        intl,
                      ),
                    );
                  }
                }}
              />
            </div>
          ),
          key: sections?.length + 1,
        });
        setSectionsMenu(sections);
        res?.sections && setSelectedSection(res?.sections[0]);
      }, true),
    );
  };

  return (
    <div className="h-[83vh]">
      <div
        className="h-40 flex flex-col p-8"
        style={{ justifyContent: 'flex-end', background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)' }}
      >
        <div className="text-white font-black text-2xl">
          <FormattedMessage id="CREATE_TASK" defaultMessage="Create Task" />
        </div>
      </div>
      <div className="p-5 flex flex-col align-center justify-between h-full">
        <div className="w-full">
          <div className="font-14 font-bold mt-1 mb-2">
            <FormattedMessage id="TASK_DETAILS" defaultMessage="Task Details" />
          </div>
          <Input
            className="font-16 py-2 mb-3"
            style={{ border: '1.5px solid rgba(99,102,241,0.35)', borderRadius: 8 }}
            placeholder={intl.formatMessage({
              id: 'TASK_NAME',
              defaultMessage: 'Task Name',
            })}
            onChange={(e) => setTaskName(e.target.value)}
            maxLength={300}
            value={taskName}
          />
          <div className="my-3 rounded-lg flex flex-col" style={{ minHeight: 120, maxHeight: '35vh', border: '1.5px solid rgba(99,102,241,0.25)' }}>
            <TextEditorWrapper
              large
              placeholder={intl.formatMessage({
                id: 'ADD_DESCRIPTION',
                defaultMessage: 'Add a description...',
              })}
              onChange={(e) => setTaskDescription(e)}
              content={taskNameDescription}
              initialContent={defaultTitle}
              mentionOptions={mentionOptions}
            />
          </div>
          <div className="font-14 font-bold mb-2">
            <FormattedMessage
              id="PROJECT_DETAILS"
              defaultMessage="Project Details"
            />
          </div>
          <div className="button-row d-flex">
            {/* <Dropdown
              trigger={['click']}
              menu={{
                items: projectMentu,
                style: {
                  overflow: 'scroll',
                  maxHeight: '300px'
                }
              }}>

            </Dropdown> */}
            <div className="w-full mr-3">
              <ConnectedProjectSelect
                callback={(project) => onProjectSelect(project)}
                currentValue={[selectedProject]}
              >
                <div
                  className="cursor-pointer flex justify-between items-center bg-white text-sm font-medium text-slate-800 px-4 py-2 rounded-lg duration-150 ease-in-out"
                  style={{ border: '1.5px solid rgba(99,102,241,0.35)' }}
                >
                  <div>
                    {selectedProject ? (
                      selectedProject?.title
                    ) : (
                      <FormattedMessage
                        id="SELECT_PROJECT"
                        defaultMessage="Select Project"
                      />
                    )}
                  </div>
                  <img
                    src={'/images/icons/show-more.svg'}
                    style={{ margin: '10px' }}
                    alt="icon"
                  />
                </div>
              </ConnectedProjectSelect>
            </div>
            <Dropdown
              disabled={!selectedProject}
              trigger={['click']}
              menu={{
                items: sectionsMenu,
                style: {
                  overflow: 'scroll',
                  maxHeight: '300px',
                },
              }}
            >
              <div
                className={`${!selectedProject && 'opacity-60'} cursor-pointer w-full flex justify-between items-center bg-white text-sm font-medium text-slate-800 px-4 py-2 rounded-lg duration-150 ease-in-out`}
              style={{ border: '1.5px solid rgba(99,102,241,0.35)' }}
              >
                <div>
                  {selectedSection ? (
                    <FormattedMessage
                      id={`${selectedSection.title}`
                        .replaceAll(' ', '_')
                        .toUpperCase()}
                      defaultMessage={selectedSection.title}
                    />
                  ) : (
                    <FormattedMessage
                      id="SELECT_SECTION"
                      defaultMessage="Select Section"
                    />
                  )}
                </div>
                <img
                  src={'/images/icons/show-more.svg'}
                  style={{ margin: '10px' }}
                  alt="icon"
                />
              </div>
            </Dropdown>
          </div>
          <div className="font-14 font-bold mt-4 mb-3">
            <FormattedMessage id="ADD_ASSIGNEE" defaultMessage="Add Assignee" />
          </div>
          <ConnectedAssigneeSelect
            iconSize={26}
            showNames
            currentValue={selectedAssignees}
            setCurrentValue={setSelectedAssignees}
            isAddAssignee={true}
          />
          {/* Due Date & Priority */}
          <div className="flex gap-3 mt-4">
            <div className="flex-1">
              <div className="font-14 font-bold mb-2">
                <FormattedMessage id="DUE_DATE" defaultMessage="Due Date" />
              </div>
              <DatePicker
                value={dueDate ? dayjs(dueDate) : null}
                onChange={(d) => setDueDate(d ? d.toDate() : null)}
                className="w-full rounded-lg"
                placeholder="Select date"
                format="MMM D, YYYY"
                allowClear
                style={{ height: 38, border: '1.5px solid rgba(99,102,241,0.35)' }}
              />
            </div>
            <div className="flex-1">
              <div className="font-14 font-bold mb-2">
                <FormattedMessage id="PRIORITY" defaultMessage="Priority" />
              </div>
              <Dropdown
                trigger={['click']}
                menu={{
                  items: taskPriority.map((p) => ({
                    key: p.rank,
                    label: (
                      <div className="flex items-center gap-2 px-1 py-0.5">
                        {PRIORITY_ICON[p.rank] && (
                          <img src={`/images/icons/${PRIORITY_ICON[p.rank]}.svg`} className="size-3" alt="" />
                        )}
                        <span style={{ color: p.rank !== 'nill' ? PRIORITY_COLORS[p.rank] : undefined }}>
                          {p.name}
                        </span>
                      </div>
                    ),
                    onClick: () => setPriority(p.rank),
                  })),
                }}
              >
                <div className="cursor-pointer flex justify-between items-center bg-white text-sm font-medium text-slate-800 px-4 rounded-lg duration-150" style={{ height: 38, border: '1.5px solid rgba(99,102,241,0.35)' }}>
                  <div className="flex items-center gap-2">
                    {priority !== 'nill' && PRIORITY_ICON[priority] && (
                      <img src={`/images/icons/${PRIORITY_ICON[priority]}.svg`} className="size-3" alt="" />
                    )}
                    <span style={{ color: priority !== 'nill' ? PRIORITY_COLORS[priority] : '#94a3b8' }}>
                      {priority !== 'nill' ? PRIORITY_LABEL[priority] : 'Select priority'}
                    </span>
                  </div>
                  <img src="/images/icons/show-more.svg" className="ml-2" alt="icon" />
                </div>
              </Dropdown>
            </div>
          </div>

          <div className="font-14 font-bold mt-4 mb-2">
            <FormattedMessage
              id="ADDITIONAL_DETAILS"
              defaultMessage="Additional Details"
            />
          </div>
          <div className="flex">
            {uploadedFiles &&
              uploadedFiles?.map((f) => (
                <div>
                  <img
                    alt="not found"
                    className="h-24 pr-2 object-cover"
                    width={'250px'}
                    src={URL?.createObjectURL(f)}
                  />
                  <br />
                  <button onClick={() => setUploadedFiles([])}>
                    <FormattedMessage id="REMOVE" defaultMessage="Remove" />
                  </button>
                </div>
              ))}
            {uploadedFiles?.length < 4 && (
              <>
                <input
                  style={{ display: 'none' }}
                  id="add-task-file-input"
                  type="file"
                  multiple
                  onChange={handleFileInputChange}
                />
                <label
                  htmlFor="add-task-file-input"
                  className="w-full flex align-center justify-center bg-gray-100 h-24 cursor-pointer hover:opacity-80 rounded"
                >
                  <img alt="upload files" src="/images/icons/ticket-pin.svg" className="mr-2" />
                  <FormattedMessage
                    id="UPLOAD_FILES"
                    defaultMessage="Upload Files"
                  />
                </label>
              </>
            )}
          </div>
        </div>
        <div
          onClick={(e) => {
            !isButtonDisabled && handleCreate();
          }}
          className={`w-full text-center px-4 py-3 mt-4 text-sm font-semibold text-white rounded-lg transition-colors ${isButtonDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
          style={{ background: isButtonDisabled ? 'rgba(99,102,241,0.5)' : '#4f46e5' }}
        >
          <FormattedMessage id="CREATE_TASK" defaultMessage="Create Task" />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  projects: state.projectReducer.myProjects,
  openProject: state.projectReducer.getProjects,
  defaultTitle: state.modalReducer.newTaskModal.title,
  defaultAssignee: state.modalReducer.newTaskModal.assignee,
});

export default connect(mapStateToProps)(AddTaskDrawer);
